import { createApi } from "@reduxjs/toolkit/query/react";

import { type IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";
import documentsApi from "../documents/documents";

import {
  type IGeocodeAddressRequest,
  type IGeocodeAddressResponse,
  type IReverseGeocodeRequest,
  type IReverseGeocodeResponse,
  type IDocumentGeocodingConfidenceResponse,
  type IGeocodingJobStatusResponse,
} from "./geocoding.interface";

const geocodingApi = createApi({
  reducerPath: "geocodingApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Documents],
  endpoints: (builder) => ({
    getGeocodingJobStatus: builder.query<IGeocodingJobStatusResponse, string>({
      query: (jobId) => ({
        url: `/geocoding/jobs/${jobId}/status`,
        method: "GET",
      }),
      transformResponse: (result: IApiResponse<IGeocodingJobStatusResponse>) => result.data,
    }),
    validateAddress: builder.mutation<IGeocodeAddressResponse[], IGeocodeAddressRequest>({
      query: (body) => ({
        url: "/geocoding/validate",
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<{ address: IGeocodeAddressResponse[] }>) =>
        result.data.address,
    }),
    reverseGeocode: builder.mutation<IReverseGeocodeResponse, IReverseGeocodeRequest>({
      query: (body) => ({
        url: "/geocoding/reverse",
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IReverseGeocodeResponse>) => result.data,
    }),
    getDocumentGeocodingConfidence: builder.query<IDocumentGeocodingConfidenceResponse, string>({
      query: (documentId) => ({
        url: `/geocoding/documents/${documentId}/confidence`,
        method: "GET",
      }),
      transformResponse: (result: IApiResponse<IDocumentGeocodingConfidenceResponse>) => result.data,
    }),
    restartDocumentGeocoding: builder.mutation<void, string>({
      query: (documentId) => ({
        url: `/geocoding/documents/${documentId}/restart`,
        method: "POST",
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(documentsApi.util.invalidateTags([ApiTags.Documents]));
        } catch {
          // Error is handled by the caller.
        }
      },
    }),
    restartDocumentGeofencing: builder.mutation<void, string>({
      query: (documentId) => ({
        url: `/geocoding/documents/${documentId}/geofencing/restart`,
        method: "POST",
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(documentsApi.util.invalidateTags([ApiTags.Documents]));
        } catch {
          // Error is handled by the caller.
        }
      },
    }),
  }),
});

export const {
  useGetGeocodingJobStatusQuery,
  useLazyGetGeocodingJobStatusQuery,
  useLazyGetDocumentGeocodingConfidenceQuery,
  useValidateAddressMutation,
  useReverseGeocodeMutation,
  useRestartDocumentGeocodingMutation,
  useRestartDocumentGeofencingMutation,
} = geocodingApi;
export default geocodingApi;

